import { Radio, Spin } from 'antd'
import { usePointCloudContext, ViewerMode } from 'features/pointClouds'
import {
  ContentSection,
  HeaderSection,
  ObservationPanelWrapper,
  Title,
} from 'pages/pointClouds/components/observationPanel/ObservationPanel.styles'
import { useObservations } from 'pages/pointClouds/components/observations/hooks/useObservations'
import { stopPropagation } from 'pages/pointClouds/utils'
import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import { Vector3 } from 'three'
import { GeckoSiteObservation, Severity } from 'types'

import { ObservationCard } from './ObservationCard'
import { TicketCard } from './TicketCard'

const ViewSelector = styled(Radio.Group)`
  margin-bottom: 15px;
  width: 100%;
  .ant-radio-button-wrapper {
    width: 50%;
    text-align: center;
    font-size: 13px;
    background-color: rgba(40, 40, 40, 0.8);
    border-color: rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.8);

    &:hover {
      color: white;
      border-color: rgba(255, 255, 255, 0.2);
    }

    &.ant-radio-button-wrapper-checked {
      background-color: rgba(80, 80, 80, 0.8);
      border-color: rgba(255, 255, 255, 0.2);
      color: white;
      box-shadow: none;

      &::before {
        background-color: rgba(60, 60, 60, 0.8);
      }
    }
  }
`

const TicketCardWrapper = styled.div`
  background-color: rgba(40, 40, 40, 0.8);
  border-radius: 5px;
  padding: 8px;
  margin-bottom: 5px;
  font-size: 13px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const ObservationTable = styled.div<{ isLoading?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 10px;
  height: 100%;
  overflow-y: auto;
  position: relative;
  opacity: ${(props) => (props.isLoading ? 0.5 : 1)};
  pointer-events: ${(props) => (props.isLoading ? 'none' : 'auto')};
`

const LoadingOverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
`

const NoObservations = styled.div`
  font-size: 14px;
  color: #aaa;
  text-align: center;
  padding: 20px 0;
`

const severityOrder: Record<Severity, number> = {
  [Severity.Critical]: 0,
  [Severity.High]: 1,
  [Severity.Medium]: 2,
  [Severity.Low]: 3,
}

export const ObservationPanelContent: React.FC = () => {
  const [activeView, setActiveView] = useState<'observations' | 'tickets'>(
    'observations'
  )
  const { observations, isLoadingSiteObservations, siteObservationTickets } =
    useObservations()
  const { setViewerMode, setFlyToCoordinates } = usePointCloudContext()

  const uniqueObservations = useMemo(() => {
    const uniqueObservationIds = new Set<string>()
    const filtered = observations.filter((observation) => {
      if (uniqueObservationIds.has(observation.id)) {
        return false
      }
      uniqueObservationIds.add(observation.id)
      return true
    })
    return filtered.sort((a, b) => {
      const severityA = a.data.severity ? severityOrder[a.data.severity] : 4
      const severityB = b.data.severity ? severityOrder[b.data.severity] : 4
      if (severityA !== severityB) {
        return severityA - severityB
      }
      const lastUpdatedA = a.data.lastUpdated || 0
      const lastUpdatedB = b.data.lastUpdated || 0
      return lastUpdatedB - lastUpdatedA
    })
  }, [observations])

  const flyToObservation = (observation: GeckoSiteObservation) => {
    const coordinates = observation.data?.pointCloudCoordinates?.coordinate
    if (!coordinates || coordinates.length !== 3) {
      return
    }
    setFlyToCoordinates(
      new Vector3(coordinates[0], coordinates[1], coordinates[2])
    )
    setViewerMode(ViewerMode.ENV_CONTROLS)
  }

  return (
    <ObservationPanelWrapper onWheel={stopPropagation}>
      <HeaderSection>
        <Title>Observations</Title>
        <ViewSelector
          value={activeView}
          onChange={(e) => setActiveView(e.target.value)}
          buttonStyle='solid'>
          <Radio.Button value='observations'>Observations</Radio.Button>
          <Radio.Button value='tickets'>Tickets</Radio.Button>
        </ViewSelector>
      </HeaderSection>

      <ContentSection>
        {activeView === 'observations' ? (
          <ObservationTable isLoading={isLoadingSiteObservations}>
            {isLoadingSiteObservations && (
              <LoadingOverlay>
                <Spin />
              </LoadingOverlay>
            )}
            {uniqueObservations.length === 0 ? (
              <NoObservations>No observations found</NoObservations>
            ) : (
              uniqueObservations.map((observation) => {
                return (
                  <ObservationCard
                    key={observation.id}
                    observation={observation}
                    onClick={() => {
                      flyToObservation(observation)
                    }}
                  />
                )
              })
            )}
          </ObservationTable>
        ) : (
          <ObservationTable isLoading={isLoadingSiteObservations}>
            {isLoadingSiteObservations && (
              <LoadingOverlay>
                <Spin />
              </LoadingOverlay>
            )}
            {siteObservationTickets.length === 0 ? (
              <NoObservations>No tickets found</NoObservations>
            ) : (
              siteObservationTickets.map((ticket, index) => (
                <TicketCardWrapper key={ticket.id || index}>
                  <TicketCard ticket={ticket} />
                </TicketCardWrapper>
              ))
            )}
          </ObservationTable>
        )}
      </ContentSection>
    </ObservationPanelWrapper>
  )
}
